import type { ChatTurn, Message } from '@fim/futurex-protocol';
import { createClient } from './client/factory.js';
import { augmentMessages } from './augment.js';
import { loadContext } from './context.js';
import { renderStream, usageLine } from './render.js';
import type { CliOptions } from './options.js';
import { error, info } from './ui.js';

/** One-shot: stream a single answer to a prompt, then exit. Ctrl+C cancels cleanly. */
export async function runAsk(promptText: string, opts: CliOptions): Promise<void> {
  const q = promptText.trim();
  if (!q) {
    error('usage: futurex ask "your question"');
    process.exitCode = 1;
    return;
  }

  const { client, mode } = createClient({ useRepo: opts.useRepo, useMemory: opts.useMemory });
  const ac = new AbortController();
  const onSigint = (): void => ac.abort();
  process.on('SIGINT', onSigint);

  try {
    const base: Message[] = [{ role: 'user', content: q }];
    // Gateway path: the backend OWNS the prefix + gathers context agentically via
    // tools, so we send only the conversation. Local fallback: build the prefix +
    // pre-inject retrieved chunks here (the CLI is the AI in that mode).
    let messages: Message[] = base;
    let system: string | undefined;
    if (mode === 'local') {
      const ctx = loadContext(opts);
      messages = await augmentMessages(base, ctx.retriever, opts.topK);
      system = ctx.system;
    }
    const turn: ChatTurn = {
      ...(system ? { system } : {}),
      messages,
      reasoning: opts.reasoning,
      model: opts.model,
      maxTokens: opts.maxTokens,
      ...(opts.tier ? { tier: opts.tier } : {}),
      ...(opts.effort ? { effort: opts.effort } : {}),
      // A one-shot ask is a single real user prompt — log it.
      newTurn: true,
      signal: ac.signal,
    };

    const result = await renderStream(client, turn);
    if (result.error) {
      if (ac.signal.aborted) info('cancelled');
      else {
        error(result.error);
        process.exitCode = 1;
      }
      return;
    }
    if (result.usage) info(usageLine(result.usage, opts.tier ?? 'fx-fast'));
  } finally {
    process.removeListener('SIGINT', onSigint);
  }
}
